import React, { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';
import MaterialTable, { MTableToolbar } from "material-table";
import { ThemeProvider } from '@mui/styles';
import { createTheme } from '@mui/material';
import "./dashboard.css";
import Spinner from 'components/Spinner';
import { getScreeningDashboardData, getScreenerList, getPartnerList, getListScreeningDashboardExcel } from "../../lib/axiosUtils";
import ShowToast from 'components/ShowToast';
import { TYPE_FAIL, INTERVAL } from "assets/constants/Constants";
import { CALENDAR_ICON } from "assets/constants/Icons";
import ExcelExportComponent from "components/ExcelExportComponent";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const ScreeningDashboard = () => {
    const mytheme = createTheme({});
    const ALL_OPTION = { value: "", label: "All" };
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [screenerOptions, setScreenerOptions] = useState([ALL_OPTION]);
    const [mobilizerOptions, setMobilizerOptions] = useState([ALL_OPTION]);
    const [engagementOptions, setEngagementOptions] = useState([
        { label: "CSR", value: "CSR" },
        { label: "RTD", value: "RTD" }
    ]);

    const [filters, setFilters] = useState({
        date_from: "",
        date_to: "",
        screener_id: "",
        mobilizer_id: "",
        engagement_id: ""
    });

    const [stats, setStats] = useState({
        total_screened: 0,
        selected: 0,
        not_selected: 0,
        assigned: 0
    });

    const [breakdown, setBreakdown] = useState([]);

    const handleFilterChange = (selectedOption, fieldName) => {
        setFilters((prevData) => ({
            ...prevData,
            [fieldName]: selectedOption ? selectedOption.value : ""
        }));
    };

    const handleDateChange = (event) => {
        const { name, value } = event.target;

        if (name === "date_from" && filters.date_to && value > filters.date_to) {
            setFilters((prev) => ({
                ...prev,
                date_from: value,
                date_to: value
            }));

            return;
        }

        if (name === "date_to" && filters.date_from && value < filters.date_from) {
            return;
        }

        setFilters((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const fetchDashboard = useCallback(async () => {
        try {
            setIsLoading(true);
            const params = new URLSearchParams();

            if (filters.date_from) {
                params.append("date_from", filters.date_from);
            }

            if (filters.date_to) {
                params.append("date_to", filters.date_to);
            }

            if (filters.screener_id) {
                params.append("screener_id", filters.screener_id);
            }

            if (filters.mobilizer_id) {
                params.append("partner_id", filters.mobilizer_id);
            }

            if (filters.engagement_id) {
                params.append("engagement_type", filters.engagement_id);
            }

            console.log("params:", params);
            const breakdownRes = await getScreeningDashboardData({}, {}, `?${params.toString()}`);
            // const breakdownRes = await getScreeningDashboardData({}, {});
            setBreakdown(breakdownRes?.data?.result || []);

            const data = breakdownRes?.data?.result || [];
            // const statResult = statsRes?.data?.result || {};
            const total_screened = data.reduce(
                (total, item) => total + (item.screened || 0),
                0
            );

            const selected = data.reduce(
                (total, item) => total + (item.selected || 0),
                0
            );

            const not_selected = data.reduce(
                (total, item) => total + (item.not_selected || 0),
                0
            );

            const assigned = data.reduce(
                (total, item) => total + (item.assigned || 0),
                0
            );
            setStats({
                total_screened: total_screened || 0,
                selected: selected || 0,
                not_selected: not_selected || 0,
                assigned: assigned || 0
            });

        } catch (error) {
            console.error("Screening Dashboard Fetch Error:", error);
            ShowToast('Unable to load screening dashboard, please try again', TYPE_FAIL, INTERVAL, "", "");
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line
    }, [filters]);

    useEffect(() => {
        const getFilterOptions = async () => {
            try {
                const [screenerList, mobilizerList] = await Promise.all([
                    getScreenerList({}, {}),
                    getPartnerList({}, {}),
                ]);

                setScreenerOptions([
                    ALL_OPTION,
                    ...(screenerList?.data?.result || []).map((screener) => ({
                        label: `${screener.first_name} ${screener.last_name}`,
                        value: screener._id
                    }))
                ]);

                setMobilizerOptions([
                    ALL_OPTION,
                    ...(mobilizerList?.data?.result || []).map((mobilizer) => ({
                        label: mobilizer.training_partner,
                        value: mobilizer._id
                    }))
                ]);

                setEngagementOptions([
                    ALL_OPTION,
                    { label: "CSR", value: "CSR" },
                    { label: "RTD", value: "RTD" }
                ]);
            } catch (error) {
                console.error("Screening Dashboard Filter Options Error:", error);
            }
        };

        getFilterOptions();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const hasFromDate = Boolean(filters.date_from);
        const hasToDate = Boolean(filters.date_to);

        // If only one date is selected, don't call API
        if (hasFromDate !== hasToDate) {
            return;
        }
        fetchDashboard();
    }, [fetchDashboard, filters.date_from, filters.date_to, filters.screener_id, filters.mobilizer_id, filters.engagement_id]);

    const handleExport = async () => {
        try {
            setIsExporting(true);

            // Sheet 1: Breakdown data
            const breakdownData = breakdown || [];

            // Sheet 2: Candidate list API
            const response = await getListScreeningDashboardExcel({}, {});

            const candidateData = response?.data?.result || [];

            const workbook = new ExcelJS.Workbook();

            // ============================
            // SHEET 1: SCREENING BREAKDOWN
            // ============================

            const breakdownSheet = workbook.addWorksheet(
                "Screening Breakdown"
            );

            const breakdownColumns = [
                { header: "Date", key: "date", width: 15 },
                { header: "Screener", key: "screener", width: 25 },
                { header: "Screened", key: "screened", width: 15 },
                { header: "Selected", key: "selected", width: 15 },
                { header: "Not selected", key: "not_selected", width: 18 },
                { header: "Assigned", key: "assigned", width: 15 }
            ];

            breakdownSheet.columns = breakdownColumns;

            breakdownData.forEach((item) => {
                breakdownSheet.addRow({
                    date: item.date
                        ? new Date(item.date).toLocaleDateString("en-IN")
                        : "",

                    screener: item.screener || "",

                    screened: item.screened || 0,

                    selected: item.selected || 0,

                    not_selected: item.not_selected || 0,

                    assigned: item.assigned || 0
                });
            });

            // ============================
            // SHEET 2: CANDIDATE LIST
            // ============================

            const candidateSheet = workbook.addWorksheet(
                "Screening Candidate List"
            );

            const candidateColumns = [
                { header: "S.No", key: "sno" },
                { header: "Candidate", key: "candidate", width: 30 },
                { header: "DOB", key: "date_of_birth", width: 10 },
                { header: "Contact no", key: "mobile_no", width: 10 },
                { header: "Aadhar No", key: "adhaar_no", width: 20 },
                { header: "Year of Qualification", key: "passing_year", width: 5 },
                { header: "Email ID", key: "email", width: 20 },
                { header: "Highest Qualification", key: "qualification", width: 15 },
                { header: "Gender", key: "gender", width: 5 },
                { header: "Candidate State", key: "state", width: 20 },
                { header: "Candidate Home city", key: "city", width: 15 },
                { header: "Primary occupation of the earning parent", key: "parent_occupation", width: 20 },
                { header: "Annual Income", key: "annual_income", width: 15 },
                
                { header: "Date of screening", key: "screening_date", width: 15 },
                { header: "Screener", key: "screener", width: 25 },
                { header: "Grooming", key: "grooming_score", width: 5 },
                { header: "Confidence", key: "confidence_score", width: 5 },
                { header: "Communication skills", key: "communication_score", width: 5 },
                { header: "Knowledge", key: "knowledge_score", width: 5 },
                { header: "Acceptance to relocate", key: "acceptance_to_relocate", width: 10 },
                { header: "Screener notes", key: "screener_notes", width: 30 },
                { header: "Training Partner", key: "mobilizer", width: 25 },
                { header: "Screening Status", key: "screening_status", width: 10 },
                { header: "Engagement", key: "engagement_type", width: 5 },
                { header: "Assigned Project", key: "assigned_project_name", width: 30 }
            ];

            candidateSheet.columns = candidateColumns;

            candidateData.forEach((item, index) => {
                candidateSheet.addRow({
                    sno: index + 1,
                    candidate: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
                    date_of_birth: item.date_of_birth ? new Date(item.date_of_birth).toLocaleDateString("en-IN"): "",
                    mobile_no: item.mobile_no || "",
                    adhaar_no: item.adhaar_no || "",
                    passing_year: item.passing_year || "",
                    email: item.email || "",
                    qualification: item.qualification || "",
                    gender: item.gender || "",
                    state: item.state || "",
                    city: item.city || "",
                    parent_occupation: item.parent_occupation || "",
                    annual_income: item.annual_income || "",
                    screener: screenerOptions.find((scr) => scr.value === item.screener_id)?.label,
                    grooming_score: item.grooming_score || "",
                    confidence_score: item.confidence_score || "",
                    communication_score: item.communication_score || "",
                    knowledge_score: item.knowledge_score || "",
                    acceptance_to_relocate: item.acceptance_to_relocate || "",
                    screener_notes: item.screener_notes || "",
                    mobilizer: item.training_partner || "",
                    screening_date: item.screening_date
                        ? new Date(item.screening_date).toLocaleDateString("en-IN")
                        : "",
                    screening_status: item.screening_status || "",
                    engagement_type: item.engagement_type || "",
                    assigned_project_name: item.assigned_project_name || ""
                });
            });

            // ============================
            // FORMAT BOTH SHEETS
            // ============================

            [breakdownSheet, candidateSheet].forEach((sheet) => {

                // Header formatting
                const headerRow = sheet.getRow(1);

                headerRow.font = {
                    bold: true
                };

                headerRow.alignment = {
                    vertical: "middle",
                    horizontal: "center"
                };

                headerRow.height = 25;

                // Freeze header
                sheet.views = [
                    {
                        state: "frozen",
                        ySplit: 1
                    }
                ];

                // Border and alignment
                sheet.eachRow((row) => {
                    row.eachCell((cell) => {
                        cell.alignment = {
                            vertical: "middle"
                        };

                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        };
                    });
                });
            });

            // ============================
            // DOWNLOAD EXCEL
            // ============================

            const buffer = await workbook.xlsx.writeBuffer();

            const blob = new Blob(
                [buffer],
                {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                }
            );

            saveAs(
                blob,
                `Screening_Dashboard_Candidate_Level_Detail_${new Date().toLocaleString("en-IN").replace(/[/:,\s]/g, "_")}.xlsx`
            );

        } catch (error) {
            console.error("Screening Dashboard Export Error:", error);

            ShowToast(
                "Unable to export screening data, please try again",
                TYPE_FAIL,
                INTERVAL,
                "",
                ""
            );
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            {
                isLoading ? (
                    <Spinner />
                ) : (
                    <div className="main-content">
                        <div className="back">
                            <ul className="breadcrumb1">
                                {/* <li><a href="/tjp-screening-queue">Screening queue</a></li> */}
                                <li>Screening dashboard</li>
                            </ul>
                        </div>

                        <div className="add-user-wrapper">
                            <div className="mx-auto text-left border dashboard-outer-div">

                                {/* <div className="user-icon-wrapper">
                                    <h4>Screening dashboard</h4>
                                </div> */}

                                <div className="row mt-3 screening-filter-row">
                                    <div className="input-label-group col-md-2">
                                        <label className="form-label">From</label>

                                        <div className="screening-date-wrapper">
                                            <input
                                                type="date"
                                                name="date_from"
                                                value={filters.date_from}
                                                onChange={handleDateChange}
                                                max={new Date().toISOString().split("T")[0]}
                                                className="form-control screening-filter-control"
                                            />

                                            <i className={`${CALENDAR_ICON} screening-calendar-icon`}></i>
                                        </div>
                                    </div>

                                    <div className="input-label-group col-md-2">
                                        <label className="form-label">To</label>

                                        <div className="screening-date-wrapper">
                                            <input
                                                type="date"
                                                name="date_to"
                                                value={filters.date_to}
                                                onChange={handleDateChange}
                                                min={filters.date_from || undefined}
                                                max={new Date().toISOString().split("T")[0]}
                                                className="form-control screening-filter-control"
                                            />

                                            <i className={`${CALENDAR_ICON} screening-calendar-icon`}></i>
                                        </div>
                                    </div>

                                    {/* <div className="input-label-group col-md-2">
                                        <label className="form-label">To</label>
                                        <input
                                            type="date"
                                            name="date_to"
                                            value={filters.date_to}
                                            onChange={handleDateChange}
                                            className="form-control screening-filter-control"
                                        />
                                    </div> */}

                                    <div className="input-label-group col-md-2">
                                        <label className="form-label">Screener</label>
                                        <Select
                                            className="screening-select"
                                            classNamePrefix="screening-select"
                                            placeholder="All"
                                            options={screenerOptions}
                                            value={
                                                screenerOptions.find(
                                                    option => option.value === filters.screener_id
                                                ) || ALL_OPTION
                                            }
                                            onChange={(selectedOption) =>
                                                handleFilterChange(selectedOption, "screener_id")
                                            }
                                        />
                                    </div>

                                    <div className="input-label-group col-md-2">
                                        <label className="form-label">Training Partner</label>
                                        <Select
                                            className="screening-select"
                                            classNamePrefix="screening-select"
                                            placeholder="All"
                                            options={mobilizerOptions}
                                            value={
                                                mobilizerOptions.find(
                                                    option => option.value === filters.mobilizer_id
                                                ) || ALL_OPTION
                                            }
                                            onChange={(selectedOption) =>
                                                handleFilterChange(selectedOption, "mobilizer_id")
                                            }
                                        />
                                    </div>

                                    <div className="input-label-group col-md-2">
                                        <label className="form-label">Engagement</label>
                                        <Select
                                            className="screening-select"
                                            classNamePrefix="screening-select"
                                            placeholder="All"
                                            options={engagementOptions}
                                            value={
                                                engagementOptions.find(
                                                    option => option.value === filters.engagement_id
                                                ) || ALL_OPTION
                                            }
                                            onChange={(selectedOption) =>
                                                handleFilterChange(selectedOption, "engagement_id")
                                            }
                                        />
                                    </div>

                                    <div className="col-md-2 screening-download-wrapper">
                                        <button
                                            type="button"
                                            className="brand-button screening-filter-button w-100"
                                            onClick={handleExport}
                                            disabled={isExporting}
                                        >
                                            {isExporting ? "Exporting..." : "Download Excel"}
                                        </button>
                                    </div>

                                </div>

                                <div className="row mt-4">
                                    <div className="col-md-3 col-sm-6 mb-3">
                                        <div className="card h-100 shadow-sm">
                                            <div className="card-body text-center">
                                                <div className="text-muted small">Total screened</div>
                                                <div className="h3 mb-0 mt-1">{stats.total_screened}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3 col-sm-6 mb-3">
                                        <div className="card h-100 shadow-sm border-success">
                                            <div className="card-body text-center">
                                                <div className="text-muted small">Selected</div>
                                                <div className="h3 mb-0 mt-1 text-success">{stats.selected}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3 col-sm-6 mb-3">
                                        <div className="card h-100 shadow-sm border-danger">
                                            <div className="card-body text-center">
                                                <div className="text-muted small">Not selected</div>
                                                <div className="h3 mb-0 mt-1 text-danger">{stats.not_selected}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3 col-sm-6 mb-3">
                                        <div className="card h-100 shadow-sm">
                                            <div className="card-body text-center">
                                                <div className="text-muted small">Assigned to project</div>
                                                <div className="h3 mb-0 mt-1">{stats.assigned}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row mt-2 mb-4">
                                    <div className="col-md-12">
                                        <div className="screening-breakdown-header">
                                            <p className="form-label mb-0">
                                                {/* Breakdown by screener and date */}
                                            </p>
                                        </div>

                                        <div className="screening-table-wrapper">
                                            <ThemeProvider theme={mytheme}>
                                                <MaterialTable
                                                    columns={[
                                                        {
                                                            title: "Date",
                                                            field: "date",
                                                            filtering: true,
                                                            render: rowData => {
                                                                if (!rowData.date) return "";

                                                                const date = new Date(rowData.date);

                                                                return date.toLocaleDateString("en-GB", {
                                                                    day: "2-digit",
                                                                    month: "short",
                                                                    year: "numeric"
                                                                });
                                                            },
                                                            cellStyle: {
                                                                fontSize: 14,
                                                                fontWeight: 500,
                                                                whiteSpace: "nowrap"
                                                            }
                                                        },
                                                        {
                                                            title: "Screener",
                                                            field: "screener",
                                                            filtering: true,
                                                            cellStyle: {
                                                                fontSize: 14,
                                                                fontWeight: 500
                                                            }
                                                        },
                                                        {
                                                            title: "Screened",
                                                            field: "screened",
                                                            type: "numeric",
                                                            filtering: false,
                                                            cellStyle: {
                                                                fontSize: 14,
                                                                textAlign: "center"
                                                            },
                                                            headerStyle: {
                                                                textAlign: "center"
                                                            }
                                                        },
                                                        {
                                                            title: "Selected",
                                                            field: "selected",
                                                            type: "numeric",
                                                            filtering: false,
                                                            cellStyle: {
                                                                fontSize: 14,
                                                                textAlign: "center",
                                                                color: "#198754",
                                                                fontWeight: 600
                                                            },
                                                            headerStyle: {
                                                                textAlign: "center"
                                                            }
                                                        },
                                                        {
                                                            title: "Not selected",
                                                            field: "not_selected",
                                                            type: "numeric",
                                                            filtering: false,
                                                            cellStyle: {
                                                                fontSize: 14,
                                                                textAlign: "center",
                                                                color: "#dc3545",
                                                                fontWeight: 600
                                                            },
                                                            headerStyle: {
                                                                textAlign: "center"
                                                            }
                                                        },
                                                        {
                                                            title: "Assigned",
                                                            field: "assigned",
                                                            type: "numeric",
                                                            filtering: false,

                                                            cellStyle: {
                                                                fontSize: 14,
                                                                textAlign: "center",
                                                                fontWeight: 600
                                                            },
                                                            headerStyle: {
                                                                textAlign: "center"
                                                            }
                                                        }
                                                    ]}
                                                    data={breakdown}
                                                    options={{
                                                        filtering: true,
                                                        sorting: true,
                                                        search: true,
                                                        columnsButton: true,
                                                        exportButton: true,
                                                        pageSize: 5,
                                                        pageSizeOptions: [10, 20, 50],
                                                        paginationType: "stepped",
                                                        headerStyle: {
                                                            backgroundColor: "#f8f9fa",
                                                            color: "#555",
                                                            fontWeight: 600,
                                                            fontSize: 14,
                                                            whiteSpace: "nowrap"
                                                        },
                                                        rowStyle: {
                                                            fontSize: 14
                                                        },
                                                        searchFieldStyle: {
                                                            fontSize: 14
                                                        },
                                                        actionsColumnIndex: -1,
                                                        exportFileName: "Breakdown by screener and date",
                                                    }}
                                                    localization={{
                                                        body: {
                                                            emptyDataSourceMessage:
                                                                "No screening activity in this range"
                                                        },
                                                        toolbar: {
                                                            searchPlaceholder: "Search..."
                                                        },
                                                        pagination: {
                                                            labelRowsSelect: "rows"
                                                        }
                                                    }}

                                                    components={{
                                                        Toolbar: (props) => (
                                                            <div>
                                                                <MTableToolbar {...props} />
                                                                {
                                                                    breakdown.length > 0 && (
                                                                        <ExcelExportComponent {...props} />
                                                                    )
                                                                }
                                                            </div>
                                                        ),
                                                    }}
                                                />
                                            </ThemeProvider>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div className="row dummy_row"></div>
                    </div>
                )
            }
        </>
    );
};

export default ScreeningDashboard;